import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Lock, ShieldCheck, AlertCircle, X } from "lucide-react";
import Header from "@/components/Header";
import BottomBar from "@/components/BottomBar";
import CardSwiper from "@/components/CardSwiper";

export default function CardLock() {
  const navigate = useNavigate();
  const [isLocking, setIsLocking] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleLockCard = () => {
    setShowConfirmModal(true);
  };

  const confirmLockCard = () => {
    setIsLocking(true);
    setShowConfirmModal(false);
    // Simulate API call
    setTimeout(() => {
      setIsLocking(false);
      navigate("/mycard");
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header showBack={true} onBackClick={() => navigate("/mycard")} />

      <div className="max-w-md mx-auto px-3 pb-20">
        {/* Card Display */}
        <div className="mt-4">
          <CardSwiper userEmail="user@example.com" />
        </div>

        {/* Lock Card Section */}
        <Card className="mt-6 bg-card/80">
          <CardContent className="p-6">
            <div className="space-y-4">
              {/* Icon and Title */}
              <div className="flex flex-col items-center text-center space-y-3">
                <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-full">
                  <Lock className="w-8 h-8 text-red-600 dark:text-red-400" />
                </div>
                <h2 className="text-xl font-semibold">Lock Your Card</h2>
                <p className="text-sm text-muted-foreground">
                  Temporarily disable your card to prevent unauthorized
                  transactions
                </p>
              </div>

              {/* Warning Message */}
              <div className="p-4 rounded-lg border border-yellow-300 dark:border-yellow-800 bg-yellow-900/60">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                      Important Information
                    </p>
                    <ul className="text-xs text-yellow-700 dark:text-yellow-300 space-y-1">
                      <li>• Your card will be immediately disabled</li>
                      <li>
                        • All online and offline transactions will be blocked
                      </li>
                      <li>• ATM withdrawals will not be possible</li>
                      <li>• You can unlock your card at any time</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Reasons for Locking */}
              <div className="space-y-3">
                <h3 className="text-sm font-medium">
                  Common reasons to lock your card:
                </h3>
                <div className="space-y-2">
                  {[
                    "Card is lost or misplaced",
                    "Suspicious activity detected",
                    "Temporary security measure",
                    "Not using the card for a while",
                  ].map((reason, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {reason}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3 pt-2">
                <Button
                  onClick={handleLockCard}
                  disabled={isLocking}
                  className="w-full h-12 bg-red-600 hover:bg-red-700 text-white"
                >
                  {isLocking ? "Locking Card..." : "Lock Card Now"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Additional Info */}
        {/* <Card className="mt-4 bg-muted/50">
          <CardContent className="p-4">
            <p className="text-xs text-center text-muted-foreground">
              Need help? Contact our support team 24/7 at support@fellascard.com
            </p>
          </CardContent>
        </Card> */}
      </div>

      <BottomBar />

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/75 p-4">
          <div className="bg-background rounded-[20px] shadow-lg p-6 w-full max-w-[360px]">
            {/* Close Button */}
            <div className="flex justify-end mb-4">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="p-1 hover:bg-muted rounded-full"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="space-y-6">
              {/* Icon and Title */}
              <div className="flex flex-col items-center text-center space-y-3">
                <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-full">
                  <Lock className="w-10 h-10 text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-xl font-semibold">Confirm Card Lock</h3>
                <p className="text-sm text-muted-foreground">
                  Are you sure you want to lock your fellas card?
                </p>
              </div>

              {/* Warning Info */}
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="text-sm text-center">
                  Once locked, you won't be able to make any transactions until
                  you unlock it again.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1 h-11"
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmLockCard}
                  disabled={isLocking}
                  className="flex-1 h-11 bg-red-600 hover:bg-red-700 text-white"
                >
                  {isLocking ? "Locking..." : "Lock Card"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
